import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import type { CallerIdentity } from "../../api/gateway";
import {
  cartAddons,
  hotspotMessage,
  isIncluded,
  isOneTime,
  isPerAddress,
  maxQuantityOf,
  lineSavingLabel,
  savingWords,
  totalGst,
  isPinLine,
  lineAmountLabel,
  planLineMissing,
  priceLabel,
  quote,
  rupees,
  visibleOffers,
  MAX_QUANTITY,
  PIN_CHIP,
  type CartAddon,
  type CartMode,
  type CartOffer,
  type CartQuote,
  type CartSelection,
} from "../../api/cart";

/** How long the steppers wait before asking the server again. */
export const QUOTE_DEBOUNCE_MS = 300;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export const cartQuoteKey = (ns: string, mode: CartMode, accountid: string | undefined) => ["cartquote", ns, mode, accountid ?? ""];

/** The server's quote for this plan and selection, re-asked (debounced) on every change. */
export function useCartQuote(identity: CallerIdentity, ns: string, mode: CartMode, plan: string, selection: CartSelection, accountid?: string, addresses?: number) {
  const current = JSON.stringify(cartAddons(selection));
  const asked = useDebounced(current, QUOTE_DEBOUNCE_MS);
  const query = useQuery({
    queryKey: [...cartQuoteKey(ns, mode, accountid), plan, asked, addresses ?? 1],
    enabled: Boolean(identity.token && ns && plan && (mode !== "recharge" || accountid)),
    queryFn: ({ signal }) => quote(identity, ns, { plan, addons: JSON.parse(asked) as CartAddon[], mode, ...(accountid ? { accountid } : {}), ...(addresses ? { addresses } : {}) }, signal),
    placeholderData: (prev) => (prev?.plan.groupname === plan ? prev : undefined),
    retry: false,
  });
  const data = query.data && query.data.plan.groupname === plan ? query.data : null;
  return {
    query,
    /** The quote for this plan, or null. */
    data,
    /** The steppers moved and the server has not answered for them yet. */
    pending: Boolean(plan) && (asked !== current || query.isFetching),
    error: query.error ? query.error.message : null,
  };
}

export interface CartAddonsProps {
  mode: CartMode;
  /** false: an unregistered or composition operator -- no word of GST (owner 2026-10-29). */
  speakGst?: boolean;
  /** null while the first quote is on its way, or when it failed. */
  quote: CartQuote | null;
  loading: boolean;
  error: string | null;
  selection: CartSelection;
  onChange: (sel: CartSelection) => void;
  /** A refusal to show above the lines, e.g. a 409 "price changed". */
  notice?: string;
  disabled?: boolean;
  /** The plan's pin words (planFamily pinWords); the PIN chip's text, "uses 1 pin" when absent. */
  pinLabel?: string;
  /** Create: the sale's installation addresses; a per-address one-time charge goes up to it (owner PC-1/PC-2). */
  addresses?: number;
}

/**
 * The add-ons block of Create subscriber and Recharge: offered add-ons with
 * quantity steppers, then the server's lines and total. No price is computed
 * here; every amount is the quote's.
 */
export function CartAddons(p: CartAddonsProps) {
  const q = p.quote;
  const offers = visibleOffers(q, p.mode);
  const hotspot = hotspotMessage(q);
  // Owner 2026-10-27: a one-time charge is charged once per sale, or once per
  // installation address when charged per address (radaccess refuses more).
  const maxOf = (o: CartOffer) => maxQuantityOf(o, p.addresses);
  const set = (id: string, n: number) => {
    const o = offers.find((x) => x.component_id === id);
    p.onChange({ ...p.selection, [id]: Math.min(o ? maxOf(o) : MAX_QUANTITY, Math.max(0, n)) });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {p.notice && <Alert severity="warning">{p.notice}</Alert>}
      {p.error && <Alert severity="error">Add-ons could not be priced: {p.error}</Alert>}
      {!q && p.loading && (
        <Typography variant="body2" color="text.secondary">
          Pricing…
        </Typography>
      )}
      {hotspot && <Alert severity="info">{hotspot}</Alert>}
      {offers.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Add-ons</Typography>
          {offers.map((o) => {
            const n = p.selection[o.component_id] ?? 0;
            if (isIncluded(o)) {
              // Comes with the plan: the server puts it in the cart; nothing to choose.
              return (
                <Box key={o.component_id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25, flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                    <Typography variant="body2">{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.list_price ? `worth ${rupees(o.list_price)} · ` : ""}
                      {isOneTime(o) ? "one-time" : "this term"}
                    </Typography>
                  </Box>
                  <Chip size="small" color="success" variant="outlined" label="Included with the plan" />
                </Box>
              );
            }
            return (
              <Box key={o.component_id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                  <Typography variant="body2">{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {o.list_price ? (
                      <>
                        <s>{rupees(o.list_price)}</s>{" "}
                      </>
                    ) : null}
                    {rupees(o.price)} · {isOneTime(o) ? "one-time" : "this term"}
                    {isPerAddress(o) ? ` · once per installation address (${Math.max(1, p.addresses ?? 1)} in this sale)` : ""}
                    {o.list_price && o.saving_reason ? ` · ${savingWords(o.saving_reason)}` : ""}
                    {o.remembered_quantity > 0 ? ` · had ${o.remembered_quantity} last time` : ""}
                  </Typography>
                </Box>
                <IconButton size="small" aria-label={`Fewer ${o.name}`} disabled={p.disabled || n <= 0} onClick={() => set(o.component_id, n - 1)}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" component="span" sx={{ minWidth: 20, textAlign: "center" }} data-quantity={o.component_id}>
                  {n}
                </Typography>
                <IconButton size="small" aria-label={`More ${o.name}`} disabled={p.disabled || n >= maxOf(o)} onClick={() => set(o.component_id, n + 1)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
      {q && (
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}>
          {planLineMissing(q) && (
            <LineRow label={q.plan.name || q.plan.groupname} amount={priceLabel(q.plan.price)} pin={q.plan.pin === "spends" ? p.pinLabel || PIN_CHIP : ""} />
          )}
          <Typography variant="subtitle2">Review</Typography>
          {q.lines.map((l) => (
            <LineRow
              key={l.component_id}
              label={l.quantity > 1 ? `${l.name} × ${l.quantity}` : l.name}
              amount={lineAmountLabel(q, l)}
              pin={isPinLine(q, l) ? p.pinLabel || PIN_CHIP : ""}
              chip={l.included ? "included" : l.billing_type === "ONE_TIME" ? "one-time" : ""}
              note={lineSavingLabel(l)}
            />
          ))}
          <LineRow label="Total" amount={priceLabel(q.total)} strong />
          {q.total != null && <ReviewNotes q={q} speakGst={p.speakGst ?? true} />}
          {p.loading && (
            <Typography variant="caption" color="text.secondary">
              Re-pricing…
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/** Under the total: the GST inside it, what the cart saves, and what the next term costs. */
function ReviewNotes({ q, speakGst }: { q: CartQuote; speakGst: boolean }) {
  const gst = speakGst ? totalGst(q) : null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {gst != null && (
        <Typography variant="caption" color="text.secondary">
          Includes GST {rupees(gst)}
        </Typography>
      )}
      {q.saving != null && q.saving > 0 && (
        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
          You save {rupees(q.saving)}
        </Typography>
      )}
      {q.renewal != null && (
        <Typography variant="caption" color="text.secondary">
          Renews at {rupees(q.renewal)} per term at today's prices
        </Typography>
      )}
    </Box>
  );
}

function LineRow({ label, amount, strong, pin, chip, note }: { label: string; amount: string; strong?: boolean; pin?: string; chip?: string; note?: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: strong ? 600 : undefined }}>
          {label}
        </Typography>
        {pin && <Chip size="small" variant="outlined" label={pin} />}
        {chip && <Chip size="small" variant="outlined" label={chip} />}
        {note && (
          <Typography variant="caption" color="success.main">
            {note}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: strong ? 600 : undefined, whiteSpace: "nowrap" }}>
        {amount}
      </Typography>
    </Box>
  );
}
